import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import cx from 'classnames';
import Head from 'next/head';

import { Link, Spinner } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import { Page } from 'components/layouts';
import { MatchesProgress } from 'components/candidate/matches';
import { getLayout } from 'components/layouts/Header';
import { TodoSection } from 'pages/candidate/dashboard/TodoSection/TodoSection.component';
import { withAuthCandidate } from 'lib/hocs/withAuthCandidate';
import { LOCALE_PAGES, fetchTranslationFiles } from 'lib/services/page';
import { useCandidateMatches } from 'lib/providers/CandidateMatchesProvider';
import { PAGE_ROUTES } from 'lib/page-routes';

import styles from '../NoMatches.module.scss';

const CandidateMatchesSeenAll = () => {
  const [t] = useTranslation('candidate.matches.seen-all');
  const { matchesMeta, isMatchesLoading } = useCandidateMatches();
  const { replace } = useRouter();

  useEffect(() => {
    if (!matchesMeta) return;
    const { total_count, viewed_count } = matchesMeta;
    if (viewed_count < total_count) {
      replace(PAGE_ROUTES.CANDIDATE_MATCHES);
    }
  }, [matchesMeta]);

  if (isMatchesLoading) return <Spinner wrapperClassname={styles.spinner} />;

  return (
    <>
      <Head>
        <title>Matches | Huzzle</title>
      </Head>
      <Page className={styles.page}>
        <MatchesProgress className={styles['matches-progress']} />
        <section className={styles.container}>
          <div className={styles.content}>
            <div className={cx(styles['main'], styles['no-image'])}>
              <p className={styles.emoji}>{t('emoji')}</p>
              <p className={styles.title}>{t('title')}</p>
              <p className={styles.description}>{t('description')}</p>
              <div className={cx(styles.actions, styles['seen-all'])}>
                <Link
                  href={PAGE_ROUTES.CANDIDATE_DASHBOARD}
                  underlineOnHover={false}
                  className={styles.btn}
                >
                  <ButtonV3 color="primary" size="large" className={styles.btn}>
                    {t('go-to-home')}
                  </ButtonV3>
                </Link>
                <Link
                  href={`${PAGE_ROUTES.CANDIDATE_ONBOARDING}?matching_preference=true`}
                  underlineOnHover={false}
                  className={styles.btn}
                >
                  <ButtonV3
                    color="secondary"
                    size="large"
                    variant="text"
                    className={styles.btn}
                  >
                    {t('change-preferences')}
                  </ButtonV3>
                </Link>
              </div>
            </div>
            <TodoSection className={styles['todo-section']} />
          </div>
        </section>
      </Page>
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await fetchTranslationFiles({
        locale,
        pages: [LOCALE_PAGES.CANDIDATE_MATCHES_SEEN_ALL],
      })),
    },
  };
}

const CandidateMatchesSeenAllWrapper = withAuthCandidate(
  CandidateMatchesSeenAll
);

// @ts-ignore
CandidateMatchesSeenAllWrapper.getLayout = getLayout;

export default CandidateMatchesSeenAllWrapper;
